import { TranslateService } from "@ngx-translate/core";
import { HttpSigningFlowService } from "../../services/http.signingflow.service";
import { NotificationService } from "../../services/notification.service";
import { throwError } from "rxjs";
import { SigningFlowResponse } from "../response/SigningFlowResponse";
import { Injectable } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { RequestsFetchState, SigningFlowStatus, SigningTaskStatus, TableFilter } from "../../constants/app.enums";
import { SignerDetail } from "../interfaces/SignerDetail";
import { SigningFlowTableData } from "../interfaces/SigningFlowTableData";
import { NGXLogger } from "ngx-logger";
import { HttpSigningTaskService } from "../../services/http.signingtask.service";

@Injectable({
    providedIn: 'root'
})
export class SigningFlowManager {
    private SELECTED_FILTER: string = window.localStorage.getItem('SELECTED_FILTER') ?? TableFilter.MY_REQUESTS_TABLE_FILTER_ALL
    private REQUEST_FETCH_STATE: RequestsFetchState = RequestsFetchState.NONE;

    private EMPTY_STRING: string = '';
    private CURRENT_TOTAL_COUNT: number = 0
    private OVERALL_TOTAL_COUNT: number = 0
    private CURRENT_PAGE: number = 0;
    private RECORDS_PER_PAGE: number = 10;
    private SIGNING_FLOWS_TABLE_DATE: SigningFlowTableData[] = []

    private refreshTableCallback?: () => void
    private refreshPaginatorAfterFilterCallback?: () => void

    TableFilter = TableFilter;
    filterOptions = [
        TableFilter.MY_REQUESTS_TABLE_FILTER_ALL, 
        TableFilter.MY_REQUESTS_TABLE_FILTER_COMPLETED, 
        TableFilter.MY_REQUESTS_TABLE_FILTER_PENDING, 
        TableFilter.MY_REQUESTS_TABLE_FILTER_CANCELLED
    ];

    constructor(
        private logger: NGXLogger,
        private notificationService: NotificationService,
        private translate: TranslateService,
        private signingFlowService: HttpSigningFlowService,
        private signingTaskService: HttpSigningTaskService
    ){}
    
    public clearValues(){
        this.SIGNING_FLOWS_TABLE_DATE = [];
        this.CURRENT_TOTAL_COUNT = 0;
        this.OVERALL_TOTAL_COUNT = 0;
        this.CURRENT_PAGE = 0;
        this.REQUEST_FETCH_STATE = RequestsFetchState.NONE;
    }
    
    public getFirstPage(){
        this.getPage(0, this.SELECTED_FILTER);
    }

    private getPage(page: number, filter: string){
        if(page < 0){
            this.notificationService.displayErrorExtra(this.translate.instant('MY_REQUESTS_NOTIFICATION_LOADING_SIGNERS_ERROR'));
            throwError(() => new Error('Parameter "page" cant be below 0!'))
        }

        this.signingFlowService.getSigningFlowsWithLimit(
            this.EMPTY_STRING,                              // no specific creator -> only admin can request by creator
            this.RECORDS_PER_PAGE * page,                   // skip
            this.RECORDS_PER_PAGE,                          // limit
            filter.replace('MY_REQUESTS_TABLE_FILTER_', '') // filter
        ).subscribe({
            next: (signingFlowsResponse) => {
                var TABLE_ID = this.SIGNING_FLOWS_TABLE_DATE.length;
                var signingFlows: SigningFlowResponse[] = signingFlowsResponse.signingFlows;
                
                this.CURRENT_TOTAL_COUNT = signingFlowsResponse.currentTotalCount;
                this.OVERALL_TOTAL_COUNT = signingFlowsResponse.overallTotalCount;
                
                signingFlows = signingFlows.sort((a, b) => {
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                });
        
                signingFlows.forEach(flow => {
                    const signingTasks: SignerDetail[] = [];
                    
                    flow.signingTasks = flow.signingTasks.sort((a, b) => {
                        if(flow.isOrdered){
                            if (a.order < b.order) return -1;
                            if (a.order > b.order) return 1;
                            return 0;
                        }
                        else{
                            const signerA = a.signer.toLowerCase();
                            const signerB = b.signer.toLowerCase();
                            
                            if (signerA < signerB) return -1;
                            if (signerA > signerB) return 1;
                            return 0;
                        }
                    });
            
                    var TASK_ID = 0;
                    flow.signingTasks.forEach(task => {
                        signingTasks.push({
                            TABLE_TASK_INDEX: TASK_ID++,
                            TABLE_TASK_EMAIL: task.signer,
                            TABLE_TASK_STATUS: this.getTaskStatusFromString(task.status)!,
                            TABLE_TASK_REFERENCE_CODE: task.referenceCode
                        });
                    })
                    
                    this.SIGNING_FLOWS_TABLE_DATE.push({
                        TABLE_ID: TABLE_ID,
                        TABLE_DATE: flow.createdAt,
                        TABLE_TITLE: flow.fileName,
                        TABLE_FLOW_STATUS: this.getFlowStatusFromString(flow.status)!,
                        TABLE_SIGNER_DETAILS: signingTasks,
                        TABLE_FLOW_REFERENCE_CODE: flow.referenceCode,
                        TABLE_FLOW_ORDERED: flow.isOrdered
                    });
                    TABLE_ID += 1; 
                });

                if(this.OVERALL_TOTAL_COUNT > 0){
                    // this.notificationService.displayError("successfully pulled: " + this.SIGNING_FLOWS_TABLE_DATE.length + "/" + this.CURRENT_TOTAL_COUNT);
                    this.REQUEST_FETCH_STATE = RequestsFetchState.FETCH_SUCCESS
                }
                else{
                    this.REQUEST_FETCH_STATE = RequestsFetchState.FETCH_SUCCESS_AND_EMPTY
                }
                this.runRefreshTableCallback()
            },
            error: (err) => {
                this.notificationService.displayErrorExtra(this.translate.instant('MY_REQUESTS_NOTIFICATION_LOADING_SIGNERS_ERROR'));
                this.REQUEST_FETCH_STATE = RequestsFetchState.FETCH_ERROR;
            },
        });
    }

    public cancelSigningFlow(referenceCode: string){
        this.signingFlowService.cancelSigningFlow(referenceCode).subscribe({
            error: (err) => {
                this.notificationService.displayErrorExtra(this.translate.instant('MY_REQUESTS_NOTIFICATION_CANCELLING_FLOW_ERROR'));
            }
        });
    }
    public resendEmail(referenceCode: string){
        this.signingTaskService.updateSigningTaskStatus({ status: SigningTaskStatus.PENDING }, referenceCode).subscribe({
            next: () => {
                this.clearValues();
                this.getFirstPage();
            }, 
            error: (err) => {
                this.notificationService.displayErrorExtra(this.translate.instant('MY_REQUESTS_NOTIFICATION_RESENDING_EMAIL_ERROR'));
            }
        })
    }

    public downloadFile(referenceCode: string){
        this.signingFlowService.getSigningFlowFile(referenceCode).subscribe({
            next: (response ) => {
                // Extract filename from Content-Disposition header
                const contentDisposition = response.headers.get('Content-Disposition');
                let fileName = 'download';
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (match?.[1]) {
                        fileName = match[1];
                    }
                }

                // Create blob and download
                const blob = new Blob([response.body!], { type: response.body!.type });
                const url = window.URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();

                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.notificationService.displayErrorExtra(this.translate.instant('MY_REQUESTS_NOTIFICATION_DOWNLOADING_FILE_ERROR'));
            }
        });
    }
    
    public getCurrentPage(): number{
        return this.CURRENT_PAGE;
    }

    public setNumberOfRecordsPerPage(recordsPerPage: number){
        this.RECORDS_PER_PAGE = recordsPerPage;
    }
    
    public getNumberOfRecordsPerPage(){
        return this.RECORDS_PER_PAGE;
    }

    public setTotalCount(currentTotalCount: number){
        this.CURRENT_TOTAL_COUNT = currentTotalCount;
    }

    public getTotalCount(): number{
        return this.CURRENT_TOTAL_COUNT;
    }

    public setRefreshTableCallback(refreshTableCallback: () => void){
        this.refreshTableCallback = refreshTableCallback;
    }

    public setRefreshPaginatorAfterFilterCallback(refreshPaginatorAfterFilterCallback: () => void){
        this.refreshPaginatorAfterFilterCallback = refreshPaginatorAfterFilterCallback;
    }

    public onPageEvent(event: PageEvent){
        this.CURRENT_PAGE = event.pageIndex;

        if(event.pageSize !== this.RECORDS_PER_PAGE){
            this.RECORDS_PER_PAGE = event.pageSize;
            this.SIGNING_FLOWS_TABLE_DATE = [];
            this.getPage(event.pageIndex, this.SELECTED_FILTER);
        }
        else if(
            this.SIGNING_FLOWS_TABLE_DATE.length < (event.pageIndex + 1) * this.RECORDS_PER_PAGE &&
            this.SIGNING_FLOWS_TABLE_DATE.length < this.CURRENT_TOTAL_COUNT
        ){
            this.getPage(event.pageIndex, this.SELECTED_FILTER);
        }
        else{
            this.runRefreshTableCallback()
        }
    }

    public getFetchState(): RequestsFetchState{
        return this.REQUEST_FETCH_STATE;
    }

    public getSelectedFilter(): string{
        return this.SELECTED_FILTER;
    }

    public getSigningFlowTableData(): SigningFlowTableData[]{
        var start = this.CURRENT_PAGE * this.RECORDS_PER_PAGE;
        var end = (this.CURRENT_PAGE + 1) * this.RECORDS_PER_PAGE < this.CURRENT_TOTAL_COUNT ? (this.CURRENT_PAGE + 1) * this.RECORDS_PER_PAGE : this.CURRENT_TOTAL_COUNT
        return this.SIGNING_FLOWS_TABLE_DATE.slice(start, end);
    }

    public onFilterSelection(option: string){
        if(this.SELECTED_FILTER === option) { return; }
        this.SELECTED_FILTER = option;
        window.localStorage.setItem('SELECTED_FILTER', option)
        this.CURRENT_PAGE = 0;
        
        this.SIGNING_FLOWS_TABLE_DATE = [];
        this.getPage(0, option);

        this.runRefreshTableCallback()
        this.runRefreshPaginatorAfterFilterCallback()
    }

    private runRefreshTableCallback(){
        if(this.refreshTableCallback !== undefined){ // ak volame z my-request tak nemame callback nastaveny
            this.refreshTableCallback()
        }
    }

    private runRefreshPaginatorAfterFilterCallback(){
        if(this.refreshPaginatorAfterFilterCallback !== undefined){ // ak volame z my-request tak nemame callback nastaveny
            this.refreshPaginatorAfterFilterCallback()
        }
    }

    private getTaskStatusFromString(state: string): SigningTaskStatus | undefined {
        if (Object.values(SigningTaskStatus).includes(state as SigningTaskStatus)) {
            return state as SigningTaskStatus;
        }
        return undefined;
    }
    
    private getFlowStatusFromString(state: string): SigningFlowStatus | undefined {
        if (Object.values(SigningFlowStatus).includes(state as SigningFlowStatus)) {
            return state as SigningFlowStatus;
        }
        return undefined;
    }
}